import React, { useEffect, useState } from "react";
import axios from "axios";
import cookie from "react-cookies";
import CurrencyFormat from "react-currency-format";
import { Spinner } from "reactstrap";
import constants from "../../Constants";
import "./DashboardInsightCards.css";

const baseURL = constants.baseURL;

const authHeaders = () => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: cookie.load("session-id-qt"),
  },
});

function formatRand(value) {
  const n = Number(value) || 0;
  return (
    <CurrencyFormat
      value={n}
      displayType="text"
      thousandSeparator
      prefix="R"
      decimalScale={2}
      fixedDecimalScale={n % 1 !== 0}
    />
  );
}

function clampLine(text, max = 110) {
  const raw = String(text || "").trim();
  if (!raw) return "";
  if (raw.length <= max) return raw;
  return raw.slice(0, max - 1).replace(/\s+\S*$/, "") + "…";
}

/**
 * Compact insight strip: Billing · Weather · next action.
 * Advice is live/ops only — no habit lectures.
 */
export default function DashboardInsightCards() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${baseURL}/customer-dashboard-insights`, authHeaders())
      .then((res) => {
        if (mounted) setData(res.data || null);
      })
      .catch(() => {
        if (mounted) setData(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const billing = data?.billing;
  const weather = data?.weather;
  const tip = data?.advice?.tips?.[0];
  const isClear = tip?.label === "All clear";
  const adviceAction = clampLine(
    tip?.action || "No booking risks flagged right now",
    90
  );
  const adviceReason = clampLine(
    tip?.reason || "We’ll nudge you when something needs attention",
    100
  );
  const weatherWarn = !!(weather?.willRain || weather?.severe);
  const adviceHref = tip?.href || null;

  const AdviceInner = (
    <>
      <div className="qt-dash-insight-icon">
        <i
          className={"mdi " + (tip?.icon || "mdi-check-circle-outline")}
          aria-hidden="true"
        />
      </div>
      <div className="qt-dash-insight-body">
        <p className="qt-dash-insight-label">{tip?.label || "Next action"}</p>
        <p className="qt-dash-insight-value qt-dash-insight-value--advice">
          {adviceAction}
        </p>
        <p className="qt-dash-insight-hint">{adviceReason}</p>
      </div>
      {adviceHref ? (
        <i
          className="mdi mdi-chevron-right qt-dash-insight-chevron"
          aria-hidden="true"
        />
      ) : null}
    </>
  );

  return (
    <div className="qt-dash-insights" aria-label="Dashboard insights">
      {loading ? (
        <div className="qt-dash-insights-loading">
          <Spinner size="sm" /> Loading insights…
        </div>
      ) : (
        <div className="qt-dash-insights-grid">
          <a
            href="/billing"
            className="qt-dash-insight qt-dash-insight--billing qt-dash-insight--link"
          >
            <div className="qt-dash-insight-icon">
              <i className="mdi mdi-receipt" aria-hidden="true" />
            </div>
            <div className="qt-dash-insight-body">
              <p className="qt-dash-insight-label">Billing</p>
              <p className="qt-dash-insight-value">
                {billing ? formatRand(billing.monthlyDue) : "—"}
              </p>
              <p className="qt-dash-insight-hint">
                {billing?.unpaidCount > 0
                  ? `${billing.unpaidCount} unpaid · this month`
                  : "Due this month · view invoices"}
              </p>
            </div>
            <i
              className="mdi mdi-chevron-right qt-dash-insight-chevron"
              aria-hidden="true"
            />
          </a>

          <div
            className={
              "qt-dash-insight qt-dash-insight--weather" +
              (weatherWarn ? " is-warn" : "")
            }
            title={weather?.alert || weather?.areaHint || ""}
          >
            <div className="qt-dash-insight-icon">
              <i
                className={
                  "mdi " + (weather?.icon || "mdi-weather-partly-cloudy")
                }
                aria-hidden="true"
              />
            </div>
            <div className="qt-dash-insight-body">
              <p className="qt-dash-insight-label">Today&apos;s weather</p>
              <p className="qt-dash-insight-value">
                {weather
                  ? `${
                      weather.temperatureC != null
                        ? `${Math.round(weather.temperatureC)}°`
                        : ""
                    }${weather.temperatureC != null ? " · " : ""}${
                      weather.condition || "Forecast unavailable"
                    }`
                  : "Unavailable"}
              </p>
              <p className="qt-dash-insight-hint">
                {weather?.alert
                  ? weather.alert
                  : weather?.areaHint || "Local forecast for your usual area"}
              </p>
            </div>
          </div>

          {adviceHref ? (
            <a
              href={adviceHref}
              className={
                "qt-dash-insight qt-dash-insight--advice qt-dash-insight--link" +
                (isClear ? " is-clear" : "")
              }
              title={tip?.text || ""}
            >
              {AdviceInner}
            </a>
          ) : (
            <div
              className={
                "qt-dash-insight qt-dash-insight--advice" +
                (isClear ? " is-clear" : "")
              }
              title={tip?.text || ""}
            >
              {AdviceInner}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
